/**
 * External dependencies
 */
import { ContentBlock, ContentState, EditorState } from 'draft-js';
import { v4 as uuidv4 } from 'uuid';

/**
 * Internal dependencies
 */
import {
  FONT_REFERENCE,
  FONT_SIZE,
  LINE_HEIGHT,
  ITALIC,
  NORMAL,
  WEIGHT,
} from './customConstants';
import { fontReferenceToStyle } from './formatters/fontFamily';
import { fontSizeToStyle } from './formatters/fontSize';
import { lineHeightToStyle } from './formatters/lineHeight';
import { weightToStyle } from './formatters/weight';
import {
  FONT_REFERENCE_ATTRIBUTE,
  ensureTextFontReference,
  getTextFontIdentityKey,
  getTextFontReferenceMap,
  normalizeTextFontReferences,
  parseInlineFontSize,
  parseInlineLineHeight,
  pruneUnusedFontReferences,
  revalidateTextFont,
  revalidateTextFontReferences,
} from '../../elements/text/utils/mixedTypography';
import customExport from './customExport';
import customImport from './customImport';

export const MIXED_TYPOGRAPHY_CLIPBOARD_MIME_TYPE =
  'application/x-storyteller-text-runs+json';

const CLIPBOARD_VERSION = 1;
const MAX_MIME_DATA_LENGTH = 1_000_000;
const MAX_CONTENT_LENGTH = 500_000;
const MAX_HTML_SIDECAR_LENGTH = MAX_MIME_DATA_LENGTH * 12;
const MAX_CATALOG_FONTS = 1_000;
const MAX_FONT_STRING_LENGTH = 512;
const HTML_SIDECAR_SENTINEL = 'storyteller_text_runs';
const HTML_SIDECAR_ATTRIBUTE = 'data-storyteller-text-runs';
const CLIPBOARD_SESSION_ID = uuidv4();
const FONT_SERVICES = new Set(['custom', 'fonts.google.com', 'system']);
const FONT_STYLES = new Set(['normal', 'italic', 'regular']);
const TEXT_FONT_STYLES = new Set(['normal', 'italic']);
const FONT_METRIC_KEYS = [
  'upm',
  'asc',
  'des',
  'tAsc',
  'tDes',
  'tLGap',
  'wAsc',
  'wDes',
  'xH',
  'capH',
  'yMin',
  'yMax',
  'hAsc',
  'hDes',
  'lGap',
];
const REQUIRED_FONT_METRIC_KEYS = ['upm', 'asc', 'des'];

function isRecord(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function getBoundedString(value) {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim();
  return normalized && normalized.length <= MAX_FONT_STRING_LENGTH
    ? normalized
    : null;
}

function isFontWeight(value) {
  return (
    Number.isInteger(value) && value >= 100 && value <= 900 && value % 100 === 0
  );
}

function sanitizeStringArray(value, predicate = () => true) {
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value.map(getBoundedString).filter((item) => item && predicate(item));
}

function sanitizeWeights(value) {
  return Array.isArray(value) ? value.filter(isFontWeight) : undefined;
}

function sanitizeVariants(value) {
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value
    .filter(
      (variant) =>
        Array.isArray(variant) &&
        variant.length === 2 &&
        (variant[0] === 0 || variant[0] === 1) &&
        isFontWeight(variant[1])
    )
    .map((variant) => [variant[0], variant[1]]);
}

function sanitizeMetrics(value) {
  if (
    !isRecord(value) ||
    !REQUIRED_FONT_METRIC_KEYS.every((key) => Number.isFinite(value[key])) ||
    value.upm === 0
  ) {
    return undefined;
  }
  return Object.fromEntries(
    FONT_METRIC_KEYS.flatMap((key) =>
      Number.isFinite(value[key]) ? [[key, value[key]]] : []
    )
  );
}

function sanitizeVariantUrls(value) {
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value.flatMap((item) => {
    const url = getBoundedString(item?.url);
    const variants = sanitizeVariants([item?.variant]);
    return url && variants?.length ? [{ variant: variants[0], url }] : [];
  });
}

/**
 * Converts an extended font-picker option to the exact persisted FontData
 * shape. The returned object is the only catalog data passed to trust
 * revalidation; UI keys and arbitrary metadata never affect identity.
 */
function sanitizeCatalogFont(value) {
  if (!isRecord(value)) {
    return null;
  }

  const family = getBoundedString(value.family || value.value);
  const service = getBoundedString(value.service);
  if (!family || !service || !FONT_SERVICES.has(service)) {
    return null;
  }

  const font = { family, service };
  const optionalValues = {
    fallbacks: sanitizeStringArray(value.fallbacks),
    weights: sanitizeWeights(value.weights),
    styles: sanitizeStringArray(value.styles, (style) =>
      FONT_STYLES.has(style)
    ),
    variants: sanitizeVariants(value.variants),
    metrics: sanitizeMetrics(value.metrics),
    url: getBoundedString(value.url) || undefined,
    urls: sanitizeVariantUrls(value.urls),
    sourceId: getBoundedString(value.sourceId) || undefined,
    fsSelection: Number.isInteger(value.fsSelection)
      ? value.fsSelection
      : undefined,
    useTypoMetrics:
      typeof value.useTypoMetrics === 'boolean'
        ? value.useTypoMetrics
        : undefined,
  };

  Object.entries(optionalValues).forEach(([key, optionalValue]) => {
    if (typeof optionalValue !== 'undefined') {
      font[key] = optionalValue;
    }
  });
  return font;
}

function normalizeRegistry(fontReferences, fontReferenceMap) {
  const value =
    fontReferenceMap instanceof Map
      ? { version: CLIPBOARD_VERSION, items: [...fontReferenceMap.values()] }
      : fontReferences;
  return (
    normalizeTextFontReferences(value) || {
      version: CLIPBOARD_VERSION,
      items: [],
    }
  );
}

function getSelectedFragment(editorState) {
  if (!editorState) {
    return null;
  }
  const selection = editorState.getSelection();
  if (!selection || selection.isCollapsed()) {
    return null;
  }

  const content = editorState.getCurrentContent();
  const blocks = content.getBlocksAsArray();
  const startIndex = blocks.findIndex(
    (block) => block.getKey() === selection.getStartKey()
  );
  const endIndex = blocks.findIndex(
    (block) => block.getKey() === selection.getEndKey()
  );
  if (startIndex < 0 || endIndex < startIndex) {
    return null;
  }

  const selectedBlocks = blocks
    .slice(startIndex, endIndex + 1)
    .map((block, index, selectedBlocksArray) => {
      const startOffset = index === 0 ? selection.getStartOffset() : 0;
      const endOffset =
        index === selectedBlocksArray.length - 1
          ? selection.getEndOffset()
          : block.getLength();
      return new ContentBlock({
        key: block.getKey(),
        type: block.getType(),
        text: block.getText().slice(startOffset, endOffset),
        characterList: block.getCharacterList().slice(startOffset, endOffset),
        depth: block.getDepth(),
        data: block.getData(),
      });
    });

  return ContentState.createFromBlockArray(
    selectedBlocks,
    content.getEntityMap()
  );
}

function formatMixedTypographyHtmlSidecar(mimeData, content) {
  const encodedData = encodeURIComponent(mimeData);
  const sidecar = JSON.stringify({
    sentinel: HTML_SIDECAR_SENTINEL,
    data: encodedData.replace(/-/g, '%2D'),
  });
  // Chrome can discard custom MIME entries and HTML comments when clipboard
  // ownership moves between documents. An empty data-bearing element survives
  // the standard HTML clipboard normalization and has no visible fallback.
  return `<span ${HTML_SIDECAR_ATTRIBUTE}="${encodedData}"></span><!-- ${sidecar} -->${content}`;
}

/**
 * Reads the rich-text payload from the standard HTML clipboard fallback.
 * Browsers can omit custom MIME types when clipboard ownership changes, while
 * preserving HTML comments. The sidecar is still decoded through the same
 * validation path as the custom MIME payload.
 */
export function getMixedTypographyMimeDataFromHtml(html) {
  if (
    typeof html !== 'string' ||
    !html ||
    html.length > MAX_HTML_SIDECAR_LENGTH
  ) {
    return '';
  }

  const template = document.createElement('template');
  template.innerHTML = html;
  const sidecarElements = template.content.querySelectorAll(
    `[${HTML_SIDECAR_ATTRIBUTE}]`
  );
  for (const sidecarElement of sidecarElements) {
    try {
      const encodedData =
        sidecarElement.getAttribute(HTML_SIDECAR_ATTRIBUTE) || '';
      const mimeData = decodeURIComponent(encodedData);
      if (mimeData && mimeData.length <= MAX_MIME_DATA_LENGTH) {
        return mimeData;
      }
    } catch {
      // Ignore malformed attributes and try the legacy comment sidecar.
    }
  }
  const comments = document.createTreeWalker(
    template.content,
    /* NodeFilter.SHOW_COMMENT */ 128
  );
  let comment = comments.nextNode();
  while (comment) {
    try {
      const sidecar = JSON.parse(comment.nodeValue);
      if (
        sidecar?.sentinel === HTML_SIDECAR_SENTINEL &&
        typeof sidecar.data === 'string'
      ) {
        const mimeData = decodeURIComponent(sidecar.data);
        return mimeData.length <= MAX_MIME_DATA_LENGTH ? mimeData : '';
      }
    } catch {
      // Ignore unrelated or malformed clipboard comments.
    }
    comment = comments.nextNode();
  }
  return '';
}

/**
 * Serializes the current non-collapsed Draft selection for clipboard event
 * handlers. `mimeData` is written under the exported MIME type while the
 * other values remain the standard cross-application fallbacks.
 */
export function encodeMixedTypographyClipboard(
  editorState,
  {
    fontReferences,
    fontReferenceMap,
    defaultFont,
    defaultFontSize,
    defaultLineHeight,
    defaultFontWeight,
    defaultFontStyle,
    ...formatterContext
  } = {}
) {
  const fragment = getSelectedFragment(editorState);
  if (!fragment) {
    return null;
  }

  const normalizedReferences = normalizeRegistry(
    fontReferences,
    fontReferenceMap
  );
  const normalizedReferenceMap = getTextFontReferenceMap(normalizedReferences);
  const content = customExport(EditorState.createWithContent(fragment), {
    ...formatterContext,
    fontReferenceMap: normalizedReferenceMap,
  });
  if (typeof content !== 'string' || content.length > MAX_CONTENT_LENGTH) {
    return null;
  }

  const usedReferences = pruneUnusedFontReferences(
    content,
    normalizedReferences
  );
  const effectiveDefaultFontWeight = isFontWeight(defaultFontWeight)
    ? defaultFontWeight
    : 400;
  const effectiveDefaultFontStyle = TEXT_FONT_STYLES.has(defaultFontStyle)
    ? defaultFontStyle
    : 'normal';
  const payload = {
    version: CLIPBOARD_VERSION,
    sourceSessionId: CLIPBOARD_SESSION_ID,
    content,
    ...(usedReferences ? { fontReferences: usedReferences } : {}),
    defaults: {
      ...(defaultFont ? { font: defaultFont } : {}),
      ...(parseInlineFontSize(defaultFontSize) !== null
        ? { fontSize: Number(defaultFontSize) }
        : {}),
      ...(parseInlineLineHeight(defaultLineHeight) !== null
        ? { lineHeight: Number(defaultLineHeight) }
        : {}),
      fontWeight: effectiveDefaultFontWeight,
      fontStyle: effectiveDefaultFontStyle,
    },
  };
  const mimeData = JSON.stringify(payload);
  if (mimeData.length > MAX_MIME_DATA_LENGTH) {
    return null;
  }

  return {
    mimeType: MIXED_TYPOGRAPHY_CLIPBOARD_MIME_TYPE,
    mimeData,
    plainText: fragment.getPlainText('\n'),
    html: formatMixedTypographyHtmlSidecar(mimeData, content),
  };
}

function stableStringify(value) {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (isRecord(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) || 'null';
}

/** Returns whether a rich-text payload was copied by this Studio runtime. */
export function isMixedTypographyClipboardFromCurrentSession(mimeData) {
  if (
    typeof mimeData !== 'string' ||
    !mimeData ||
    mimeData.length > MAX_MIME_DATA_LENGTH
  ) {
    return false;
  }

  try {
    const payload = JSON.parse(mimeData);
    return payload?.sourceSessionId === CLIPBOARD_SESSION_ID;
  } catch {
    return false;
  }
}

function getCurrentReferencesByIdentity(fontReferences) {
  const referencesByIdentity = new Map();
  fontReferences.items.forEach((reference) => {
    const identityKey = getTextFontIdentityKey(reference);
    const snapshots = referencesByIdentity.get(identityKey) || new Map();
    snapshots.set(
      `${reference.revision}:${stableStringify(reference.font)}`,
      reference
    );
    referencesByIdentity.set(identityKey, snapshots);
  });
  return referencesByIdentity;
}

function remapReferenceAttributes(content, referenceIdMap) {
  const template = document.createElement('template');
  template.innerHTML = content;
  template.content
    .querySelectorAll(`[${FONT_REFERENCE_ATTRIBUTE}]`)
    .forEach((element) => {
      const sourceId = element.getAttribute(FONT_REFERENCE_ATTRIBUTE) || '';
      const destinationId = sourceId && referenceIdMap.get(sourceId);
      if (destinationId) {
        element.setAttribute(FONT_REFERENCE_ATTRIBUTE, destinationId);
      } else {
        element.removeAttribute(FONT_REFERENCE_ATTRIBUTE);
      }
      // The authoritative destination reference regenerates readable CSS.
      // Clipboard-provided family declarations never enter Draft state.
      element.style.removeProperty('font-family');
      if (!element.getAttribute('style')) {
        element.removeAttribute('style');
      }
    });
  return template.innerHTML;
}

function areFontsEqual(left, right) {
  if (!left || !right) {
    return false;
  }

  return (
    left.family === right.family &&
    left.service === right.service &&
    left.sourceId === right.sourceId &&
    left.url === right.url &&
    stableStringify(left.urls || []) === stableStringify(right.urls || [])
  );
}

function applyMissingStyle(contentState, prefixes, style) {
  if (!style) {
    return contentState;
  }

  const stylePrefixes = Array.isArray(prefixes) ? prefixes : [prefixes];

  const blockMap = contentState.getBlockMap().map((block) =>
    block.set(
      'characterList',
      block.getCharacterList().map((character) => {
        const styles = character.getStyle();
        const hasStyle = styles.some(
          (value) =>
            typeof value === 'string' &&
            stylePrefixes.some((prefix) => value.startsWith(prefix))
        );
        return hasStyle ? character : character.set('style', styles.add(style));
      })
    )
  );
  return contentState.set('blockMap', blockMap);
}

/**
 * Converts differing source box defaults into inline styles. Explicit mixed
 * runs remain authoritative. Without this step an unstyled copied run inherits
 * the destination box defaults and visibly changes after a range paste.
 */
function materializeTypographyForDestination(
  contentState,
  sourceDefaults,
  destinationDefaults,
  destinationReferences
) {
  if (!isRecord(sourceDefaults) || !isRecord(destinationDefaults)) {
    return { contentState, destinationReferences };
  }

  let nextContentState = contentState;
  let nextReferences = destinationReferences;
  const sourceFont = sourceDefaults.font;

  if (sourceFont && !areFontsEqual(sourceFont, destinationDefaults.font)) {
    const ensured = ensureTextFontReference(sourceFont, nextReferences);
    nextReferences = ensured.fontReferences;
    nextContentState = applyMissingStyle(
      nextContentState,
      FONT_REFERENCE,
      fontReferenceToStyle(ensured.reference.id)
    );
  }

  const sourceFontSize = parseInlineFontSize(sourceDefaults.fontSize);
  const destinationFontSize = parseInlineFontSize(destinationDefaults.fontSize);
  if (
    sourceFontSize !== null &&
    destinationFontSize !== null &&
    sourceFontSize !== destinationFontSize
  ) {
    nextContentState = applyMissingStyle(
      nextContentState,
      FONT_SIZE,
      fontSizeToStyle(sourceFontSize)
    );
  }

  const sourceLineHeight = parseInlineLineHeight(sourceDefaults.lineHeight);
  const destinationLineHeight = parseInlineLineHeight(
    destinationDefaults.lineHeight
  );
  if (
    sourceLineHeight !== null &&
    destinationLineHeight !== null &&
    sourceLineHeight !== destinationLineHeight
  ) {
    nextContentState = applyMissingStyle(
      nextContentState,
      LINE_HEIGHT,
      lineHeightToStyle(sourceLineHeight)
    );
  }

  if (
    isFontWeight(sourceDefaults.fontWeight) &&
    isFontWeight(destinationDefaults.fontWeight) &&
    sourceDefaults.fontWeight !== destinationDefaults.fontWeight
  ) {
    nextContentState = applyMissingStyle(
      nextContentState,
      WEIGHT,
      weightToStyle(sourceDefaults.fontWeight)
    );
  }

  if (
    TEXT_FONT_STYLES.has(sourceDefaults.fontStyle) &&
    TEXT_FONT_STYLES.has(destinationDefaults.fontStyle) &&
    sourceDefaults.fontStyle !== destinationDefaults.fontStyle
  ) {
    nextContentState = applyMissingStyle(
      nextContentState,
      [ITALIC, NORMAL],
      sourceDefaults.fontStyle === 'italic' ? ITALIC : NORMAL
    );
  }

  return {
    contentState: nextContentState,
    destinationReferences: nextReferences,
  };
}

/**
 * Parses and remaps a V1 clipboard payload. Fonts copied by this Studio runtime
 * can be preserved while their reference IDs are safely merged. Other callers
 * retain the catalog-validation path for backwards compatibility.
 */
export function decodeMixedTypographyClipboard(
  mimeData,
  {
    currentFontReferences,
    currentFontReferenceMap,
    catalogFonts = [],
    destinationDefaults,
    preserveCurrentSessionFonts = false,
  } = {}
) {
  if (
    typeof mimeData !== 'string' ||
    !mimeData ||
    mimeData.length > MAX_MIME_DATA_LENGTH
  ) {
    return null;
  }
  let payload;
  try {
    payload = JSON.parse(mimeData);
  } catch {
    return null;
  }
  if (
    !isRecord(payload) ||
    payload.version !== CLIPBOARD_VERSION ||
    typeof payload.content !== 'string' ||
    payload.content.length > MAX_CONTENT_LENGTH
  ) {
    return null;
  }
  const shouldPreserveSourceFonts =
    preserveCurrentSessionFonts &&
    payload.sourceSessionId === CLIPBOARD_SESSION_ID;
  if (preserveCurrentSessionFonts && !shouldPreserveSourceFonts) {
    return null;
  }

  const incomingReferences = normalizeTextFontReferences(
    payload.fontReferences
  ) || {
    version: CLIPBOARD_VERSION,
    items: [],
  };
  let destinationReferences = normalizeRegistry(
    currentFontReferences,
    currentFontReferenceMap
  );
  const existingDestinationIds = new Set(
    destinationReferences.items.map(({ id }) => id)
  );
  const trustedCatalog = (Array.isArray(catalogFonts) ? catalogFonts : [])
    .slice(0, MAX_CATALOG_FONTS)
    .map(sanitizeCatalogFont)
    .filter(Boolean);
  const incomingDefaults = isRecord(payload.defaults) ? payload.defaults : {};
  const sanitizedDefaultFont = sanitizeCatalogFont(incomingDefaults.font);
  const defaultFont = sanitizedDefaultFont
    ? shouldPreserveSourceFonts
      ? sanitizedDefaultFont
      : revalidateTextFont(sanitizedDefaultFont, trustedCatalog)
    : undefined;
  const defaultFontSize = parseInlineFontSize(incomingDefaults.fontSize);
  const defaultLineHeight = parseInlineLineHeight(incomingDefaults.lineHeight);
  const defaultFontWeight = isFontWeight(incomingDefaults.fontWeight)
    ? incomingDefaults.fontWeight
    : undefined;
  const defaultFontStyle = TEXT_FONT_STYLES.has(incomingDefaults.fontStyle)
    ? incomingDefaults.fontStyle
    : undefined;
  const catalogReferences = revalidateTextFontReferences(
    incomingReferences,
    trustedCatalog
  );
  const catalogReferencesById = getTextFontReferenceMap(catalogReferences);
  const currentReferencesByIdentity = getCurrentReferencesByIdentity(
    destinationReferences
  );
  const referenceIdMap = new Map();

  incomingReferences.items.forEach((incomingReference) => {
    let trustedReference = shouldPreserveSourceFonts
      ? incomingReference
      : catalogReferencesById.get(incomingReference.id);
    if (!trustedReference) {
      const currentCandidates = currentReferencesByIdentity.get(
        getTextFontIdentityKey(incomingReference)
      );
      if (currentCandidates?.size === 1) {
        trustedReference = [...currentCandidates.values()][0];
      }
    }
    if (!trustedReference) {
      return;
    }

    const ensured = ensureTextFontReference(
      trustedReference.font,
      destinationReferences
    );
    const nextReferenceMap = getTextFontReferenceMap(ensured.fontReferences);
    if (!nextReferenceMap.has(ensured.reference.id)) {
      return;
    }
    destinationReferences = ensured.fontReferences;
    referenceIdMap.set(incomingReference.id, ensured.reference.id);
  });

  let destinationReferenceMap = getTextFontReferenceMap(destinationReferences);
  const remappedContent = remapReferenceAttributes(
    payload.content,
    referenceIdMap
  );
  const importedContent = customImport(remappedContent, {
    fontReferenceMap: destinationReferenceMap,
  });
  const materialized = materializeTypographyForDestination(
    importedContent,
    {
      font: defaultFont,
      fontSize: defaultFontSize,
      lineHeight: defaultLineHeight,
      fontWeight: defaultFontWeight,
      fontStyle: defaultFontStyle,
    },
    destinationDefaults,
    destinationReferences
  );
  destinationReferences = materialized.destinationReferences;
  destinationReferenceMap = getTextFontReferenceMap(destinationReferences);
  const content = customExport(
    EditorState.createWithContent(materialized.contentState),
    { fontReferenceMap: destinationReferenceMap }
  );
  const usedDestinationIds = new Set(
    (
      pruneUnusedFontReferences(content, destinationReferences)?.items || []
    ).map(({ id }) => id)
  );
  destinationReferences = {
    version: CLIPBOARD_VERSION,
    items: destinationReferences.items.filter(
      ({ id }) => existingDestinationIds.has(id) || usedDestinationIds.has(id)
    ),
  };
  destinationReferenceMap = getTextFontReferenceMap(destinationReferences);

  return {
    content,
    fontReferences: destinationReferences,
    fontReferenceMap: destinationReferenceMap,
    defaults: {
      ...(defaultFont ? { font: defaultFont } : {}),
      ...(defaultFontSize !== null ? { fontSize: defaultFontSize } : {}),
      ...(defaultLineHeight !== null ? { lineHeight: defaultLineHeight } : {}),
      ...(defaultFontWeight ? { fontWeight: defaultFontWeight } : {}),
      ...(defaultFontStyle ? { fontStyle: defaultFontStyle } : {}),
    },
  };
}
